import {
    IClientTickets,
    IClientTicketsPerWish,
    IDashboardState,
    ILineChart,
    initialClientTickets,
    initialLineChartState,
    IPopularCategory,
    ITotalTickets,
    IUsersCountPerGender,
    IWishPopularityPerPointsAward,
    IWishPopularityPerVoucherCost,
} from "./Helper";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {SliceNames} from "../constants";
import {
    getAdminProfitPerMonth,
    getCategoriesPopularityPlot,
    getClientProfitPerMonth,
    getClientsTotalTicketsPlot,
    getClientTickets,
    getClientTicketsPerWish,
    getTicketsPerMonthPlot,
    getTotalTicketsPlot,
    getUsersCountPerGender,
    getUsersPerMonthPlot,
    getWishPopularityPerCost,
    getWishPopularityPerPointsAward,
    getWishPopularityPerPointsAwardForClient,
} from "./ActionCreators";
import {WishType} from "../wish/Helper";

const initialValue: IDashboardState = {
    categoriesPopularityPlot: [],
    categoriesPopularityPlotLoading: false,
    totalTicketsPlot: [],
    totalTicketsPlotLoading: false,
    clientTotalTicketsPlot: [],
    clientTotalTicketsPlotLoading: false,
    usersPerMonthPlot: initialLineChartState,
    usersPerMonthPlotLoading: false,
    ticketsBoughtPerMonthPlot: initialLineChartState,
    voucherTicketsPerMonthPlotLoading: false,
    entriesPerMonthPlot: initialLineChartState,
    drawTicketsPerMonthPlotLoading: false,
    wishPopularityPerVoucherCost: [],
    wishPopularityPeVoucherCostLoading: false,
    wishPopularityPerPointsAward: [],
    wishPopularityPerPointsAwardLoading: false,
    wishPopularityPerPointsAwardForClient: [],
    wishPopularityPerPointsAwardForClientLoading: false,
    usersCountPerGender: [],
    usersCountPerGenderLoading: false,
    clientTickets: initialClientTickets,
    clientTicketsLoading: false,
    clientTicketsPerWish: [],
    clientTicketsPerWishLoading: false,
    clientProfitPerMonthPlotLoading: false,
    clientProfitPerMonthPlot: initialLineChartState,
    adminProfitPerMonthPlotLoading: false,
    adminProfitPerMonthPlot: initialLineChartState
}

const dashboardSlice = createSlice({
    name: SliceNames.DASHBOARD,
    initialState: initialValue,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(
            getCategoriesPopularityPlot.pending, (state) => {
                state.categoriesPopularityPlotLoading = true
            })
            .addCase(
                getCategoriesPopularityPlot.fulfilled, (state, action: PayloadAction<IPopularCategory[]>) => {
                    state.categoriesPopularityPlot = action.payload
                    state.categoriesPopularityPlotLoading = false
                })
            .addCase(
                getCategoriesPopularityPlot.rejected, (state) => {
                    state.categoriesPopularityPlotLoading = false
                })
            .addCase(
                getTotalTicketsPlot.pending, (state) => {
                    state.totalTicketsPlotLoading = true
                })
            .addCase(
                getTotalTicketsPlot.fulfilled, (state, action: PayloadAction<ITotalTickets[]>) => {
                    state.totalTicketsPlot = action.payload
                    state.totalTicketsPlotLoading = false
                })
            .addCase(
                getTotalTicketsPlot.rejected, (state) => {
                    state.totalTicketsPlotLoading = false
                })
            .addCase(
                getClientsTotalTicketsPlot.pending, (state) => {
                    state.clientTotalTicketsPlotLoading = true
                })
            .addCase(
                getClientsTotalTicketsPlot.fulfilled, (state, action: PayloadAction<ITotalTickets[]>) => {
                    state.clientTotalTicketsPlot = action.payload
                    state.clientTotalTicketsPlotLoading = false
                })
            .addCase(
                getClientsTotalTicketsPlot.rejected, (state) => {
                    state.clientTotalTicketsPlotLoading = false
                })
            .addCase(
                getUsersPerMonthPlot.pending, (state) => {
                    state.usersPerMonthPlotLoading = true
                })
            .addCase(
                getUsersPerMonthPlot.fulfilled, (state, action: PayloadAction<ILineChart>) => {
                    state.usersPerMonthPlot = action.payload
                    state.usersPerMonthPlotLoading = false
                })
            .addCase(
                getUsersPerMonthPlot.rejected, (state) => {
                    state.usersPerMonthPlotLoading = false
                })
            .addCase(
                getTicketsPerMonthPlot.pending, (state, action) => {
                    const {wishType} = action.meta.arg
                    state[`${wishType}TicketsPerMonthPlotLoading`] = true
                })
            .addCase(
                getTicketsPerMonthPlot.fulfilled, (state, action: PayloadAction<{
                    data: ILineChart,
                    type: WishType
                }>) => {
                    if (action.payload.type === WishType.VOUCHER) {
                        state.ticketsBoughtPerMonthPlot = action.payload.data
                    } else {
                        state.entriesPerMonthPlot = action.payload.data
                    }
                    state[`${action.payload.type}TicketsPerMonthPlotLoading`] = false
                })
            .addCase(
                getTicketsPerMonthPlot.rejected, (state, action) => {
                    const {wishType} = action.meta.arg
                    state[`${wishType}TicketsPerMonthPlotLoading`] = false
                })
            .addCase(
                getWishPopularityPerCost.pending, (state) => {
                    state.wishPopularityPeVoucherCostLoading = true
                })
            .addCase(
                getWishPopularityPerCost.fulfilled, (state, action: PayloadAction<IWishPopularityPerVoucherCost[]>) => {
                    state.wishPopularityPerVoucherCost = action.payload
                    state.wishPopularityPeVoucherCostLoading = false
                })
            .addCase(
                getWishPopularityPerCost.rejected, (state) => {
                    state.wishPopularityPeVoucherCostLoading = false
                })
            .addCase(
                getWishPopularityPerPointsAward.pending, (state) => {
                    state.wishPopularityPerPointsAwardLoading = true
                })
            .addCase(
                getWishPopularityPerPointsAward.fulfilled, (state, action: PayloadAction<IWishPopularityPerPointsAward[]>) => {
                    state.wishPopularityPerPointsAward = action.payload
                    state.wishPopularityPerPointsAwardLoading = false
                })
            .addCase(
                getWishPopularityPerPointsAward.rejected, (state) => {
                    state.wishPopularityPerPointsAwardLoading = false
                })
            .addCase(
                getUsersCountPerGender.pending, (state) => {
                    state.usersCountPerGenderLoading = true
                })
            .addCase(
                getUsersCountPerGender.fulfilled, (state, action: PayloadAction<IUsersCountPerGender[]>) => {
                    state.usersCountPerGender = action.payload
                    state.usersCountPerGenderLoading = false
                })
            .addCase(
                getUsersCountPerGender.rejected, (state) => {
                    state.usersCountPerGenderLoading = false
                })
            .addCase(
                getClientTickets.pending, (state) => {
                    state.clientTicketsLoading = true
                })
            .addCase(
                getClientTickets.fulfilled, (state, action: PayloadAction<IClientTickets>) => {
                    state.clientTickets = action.payload
                    state.clientTicketsLoading = false
                })
            .addCase(
                getClientTickets.rejected, (state) => {
                    state.clientTicketsLoading = false
                })
            .addCase(
                getClientTicketsPerWish.pending, (state) => {
                    state.clientTicketsPerWishLoading = true
                })
            .addCase(
                getClientTicketsPerWish.fulfilled, (state, action: PayloadAction<IClientTicketsPerWish[]>) => {
                    state.clientTicketsPerWish = action.payload
                    state.clientTicketsPerWishLoading = false
                })
            .addCase(
                getClientTicketsPerWish.rejected, (state) => {
                    state.clientTicketsPerWishLoading = false
                })
            .addCase(
                getWishPopularityPerPointsAwardForClient.pending, (state) => {
                    state.wishPopularityPerPointsAwardForClientLoading = true
                })
            .addCase(
                getWishPopularityPerPointsAwardForClient.fulfilled, (state, action: PayloadAction<IWishPopularityPerPointsAward[]>) => {
                    state.wishPopularityPerPointsAwardForClient = action.payload
                    state.wishPopularityPerPointsAwardForClientLoading = false
                })
            .addCase(
                getWishPopularityPerPointsAwardForClient.rejected, (state) => {
                    state.wishPopularityPerPointsAwardForClientLoading = false
                })
            .addCase(
                getClientProfitPerMonth.pending, (state) => {
                    state.clientProfitPerMonthPlotLoading = true
                })
            .addCase(
                getClientProfitPerMonth.fulfilled, (state, action: PayloadAction<ILineChart>) => {
                    state.clientProfitPerMonthPlot = action.payload
                    state.clientProfitPerMonthPlotLoading = false
                })
            .addCase(
                getClientProfitPerMonth.rejected, (state) => {
                    state.clientProfitPerMonthPlotLoading = false
                })
            .addCase(
                getAdminProfitPerMonth.pending, (state) => {
                    state.adminProfitPerMonthPlotLoading = true
                })
            .addCase(
                getAdminProfitPerMonth.fulfilled, (state, action: PayloadAction<ILineChart>) => {
                    state.adminProfitPerMonthPlot = action.payload
                    state.adminProfitPerMonthPlotLoading = false
                })
            .addCase(
                getAdminProfitPerMonth.rejected, (state) => {
                    state.adminProfitPerMonthPlotLoading = false
                })
    }
})

export default dashboardSlice.reducer
